/*
 * Level-runes helpers — pure corpus queries for FR-INTRO-01.
 *
 * Kept in its own module (rather than tacked onto src/data/csv.ts) so the
 * intro screen has a thin, focused API to depend on and so the unit tests
 * can target synthetic corpora directly without spying on getCorpus().
 *
 * The two functions encode the rules the spec calls out:
 *   - "the 5 runes for Level N" = the first 5 type=word entries at level N
 *     in CSV order. The placeholder CSV may have fewer than 5; downstream
 *     code (the intro screen) must tolerate a short list rather than
 *     padding it.
 *   - the example word for a focal entry comes from type=word, level<=N,
 *     containing the focal rune glyph, preferring entries from the current
 *     level. The focal entry itself is excluded so the example never
 *     tautologically matches the focal rune.
 */

import type { RuneEntry } from './csv';

const MAX_RUNES_PER_LEVEL = 5;

/**
 * Return the rune entries that introduce new runes at the given level —
 * up to {@link MAX_RUNES_PER_LEVEL}, in CSV order.
 *
 * The "first rune in the rune field" is treated as the new rune; for
 * multi-rune words the additional runes are review of earlier levels.
 * Callers that need the focal glyph should read `Array.from(entry.rune)[0]`.
 */
export function getRunesForLevel(corpus: readonly RuneEntry[], level: number): RuneEntry[] {
  if (!Number.isFinite(level) || level < 1) return [];
  const matches: RuneEntry[] = [];
  for (const entry of corpus) {
    if (entry.level === level && entry.type === 'word') {
      matches.push(entry);
      if (matches.length >= MAX_RUNES_PER_LEVEL) break;
    }
  }
  return matches;
}

/**
 * Pick an example-word entry that contains the focal entry's first rune.
 *
 * Rules (mirrors the FR-INTRO-01 AC):
 *  - type === 'word'
 *  - level <= currentLevel
 *  - rune field includes the focal rune glyph
 *  - prefer entries with level === currentLevel; otherwise the first match
 *    in CSV order
 *  - exclude the focal entry itself
 *
 * Returns null when no suitable example exists; the screen renders the
 * focal rune in isolation in that case.
 */
export function findExampleWord(
  corpus: readonly RuneEntry[],
  focalEntry: RuneEntry,
  currentLevel: number,
): RuneEntry | null {
  const focalGlyph = Array.from(focalEntry.rune)[0];
  if (!focalGlyph) return null;

  let fallback: RuneEntry | null = null;
  for (const entry of corpus) {
    if (entry === focalEntry) continue;
    if (entry.type !== 'word') continue;
    if (entry.level > currentLevel) continue;
    if (!Array.from(entry.rune).includes(focalGlyph)) continue;

    if (entry.level === currentLevel) return entry;
    if (fallback === null) fallback = entry;
  }
  return fallback;
}
